import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import QRCode from "./QrPlaceholder";
import { api, ensureCsrf, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AppContext";
import { Seo } from "@/components/site/Ui";

function Shell({ title, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--ivory)] px-4 py-12">
      <Seo title={title} noindex />
      <div className="card-soft w-full max-w-md p-8 sm:p-10">
        <p className="eyebrow">Area riservata</p>
        <h1 className="mt-3 text-4xl">{title}</h1>
        {children}
      </div>
    </div>
  );
}

const hashToken = () => new URLSearchParams(window.location.hash.slice(1)).get("t") || "";

export function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", code: "", remember: false });
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await ensureCsrf();
      if (!ticket) {
        const r = await api.post("/auth/login", { email: f.email, password: f.password });
        if (r.data.mfa_required === false) { setUser(r.data.user); nav("/admin"); return; }
        setTicket(r.data.mfa_ticket);
      } else {
        const r = await api.post("/auth/mfa", { mfa_ticket: ticket, code: f.code, remember_device: f.remember });
        setUser(r.data); nav("/admin");
      }
    } catch (err) {
      setError(errorMessage(err));
      if (err.response?.data?.detail?.includes?.("ripeti")) setTicket(null);
    } finally { setBusy(false); }
  };
  return (
    <Shell title="Accedi">
      <form onSubmit={submit} className="mt-8 grid gap-4" data-testid="admin-login-form">
        {!ticket ? (
          <>
            <label className="text-sm">Email<input required type="email" autoComplete="username" className="field mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email" /></label>
            <label className="text-sm">Password<input required type="password" autoComplete="current-password" className="field mt-1" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password" /></label>
          </>
        ) : (
          <>
            <label className="text-sm">Codice dell'app di autenticazione (o codice di recupero)<input required autoFocus inputMode="numeric" autoComplete="one-time-code" className="field mt-1 tracking-[0.3em]" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="login-mfa-code" /></label>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={f.remember} onChange={(e) => setF({ ...f, remember: e.target.checked })} data-testid="login-remember-device" /> <span>Non chiedere il codice su questo dispositivo per 90 giorni <span className="block text-xs text-[var(--muted-ink)]">Solo su dispositivi personali. La password resta sempre richiesta.</span></span></label>
          </>
        )}
        {error && <p role="alert" className="text-sm text-red-700" data-testid="login-error">{error}</p>}
        <button className="btn-primary justify-center" disabled={busy} data-testid="login-submit">{ticket ? "Verifica" : "Continua"}</button>
        <Link to="/admin/password-dimenticata" className="text-center text-sm text-[var(--petrol)] underline" data-testid="login-forgot-link">Password dimenticata?</Link>
      </form>
    </Shell>
  );
}

export function AcceptInvite() {
  const [token] = useState(hashToken);
  const [setup, setSetup] = useState(null);
  const [f, setF] = useState({ name: "", password: "", code: "" });
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState(null);
  const start = async () => {
    setError(null);
    try { await ensureCsrf(); setSetup((await api.post("/auth/invite/start", { token })).data); } catch (e) { setError(errorMessage(e)); }
  };
  const complete = async (e) => {
    e.preventDefault(); setError(null);
    try { setCodes((await api.post("/auth/invite/complete", { token, ...f })).data.recovery_codes); } catch (err) { setError(errorMessage(err)); }
  };
  if (codes) {
    return (
      <Shell title="Accesso configurato">
        <p className="mt-6 text-sm">Conserva questi codici di recupero in un luogo sicuro. Ognuno può essere usato una sola volta al posto del codice dell'app. Non verranno mostrati di nuovo.</p>
        <ul className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm" data-testid="recovery-codes">{codes.map((c) => <li key={c} className="rounded bg-[var(--ivory)] px-2 py-1">{c}</li>)}</ul>
        <Link to="/admin/login" className="btn-primary mt-8 w-full justify-center" data-testid="invite-go-login">Vai al login</Link>
      </Shell>
    );
  }
  return (
    <Shell title="Configura l'accesso">
      {!token && <p className="mt-6 text-sm">Link di invito mancante o incompleto.</p>}
      {token && !setup && <><p className="mt-6 text-sm">Per proteggere il pannello è richiesta l'autenticazione a due fattori con un'app (es. Google Authenticator, Microsoft Authenticator, 1Password).</p><button className="btn-primary mt-6" onClick={start} data-testid="invite-start">Inizia</button></>}
      {setup && (
        <form onSubmit={complete} className="mt-6 grid gap-4" data-testid="invite-form">
          <p className="text-sm">Account: <strong>{setup.email}</strong> · ruolo {setup.role}</p>
          <QRCode uri={setup.otpauth_uri} secret={setup.totp_secret} />
          <label className="text-sm">Nome visualizzato<input required minLength={2} className="field mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" /></label>
          <label className="text-sm">Nuova password (min. 12 caratteri, lettere e numeri)<input required minLength={12} type="password" autoComplete="new-password" className="field mt-1" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="invite-password" /></label>
          <label className="text-sm">Codice a 6 cifre dall'app<input required pattern="[0-9]{6}" inputMode="numeric" className="field mt-1" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="invite-code" /></label>
          <button className="btn-primary justify-center" data-testid="invite-complete">Completa configurazione</button>
        </form>
      )}
      {error && <p role="alert" className="mt-4 text-sm text-red-700" data-testid="invite-error">{error}</p>}
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { await ensureCsrf(); setMsg((await api.post("/auth/forgot-password", { email })).data.message); } catch (err) { setMsg(errorMessage(err)); }
  };
  return (
    <Shell title="Recupera accesso">
      <form onSubmit={submit} className="mt-8 grid gap-4">
        <label className="text-sm">Email<input required type="email" className="field mt-1" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></label>
        <button className="btn-primary justify-center" data-testid="forgot-submit">Invia istruzioni</button>
        {msg && <p role="status" className="text-sm" data-testid="forgot-message">{msg}</p>}
        <p className="text-xs text-[var(--muted-ink)]">Se hai perso anche l'app di autenticazione e i codici di recupero, chiedi al proprietario di reimpostare il tuo accesso.</p>
      </form>
    </Shell>
  );
}

export function ResetPassword() {
  const [token] = useState(hashToken);
  const [f, setF] = useState({ password: "", code: "" });
  const [state, setState] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { await ensureCsrf(); await api.post("/auth/reset-password", { token, ...f }); setState({ ok: true }); } catch (err) { setState({ error: errorMessage(err) }); }
  };
  return (
    <Shell title="Nuova password">
      {state?.ok ? <Link to="/admin/login" className="btn-primary mt-8" data-testid="reset-done">Password aggiornata · Accedi</Link> : (
        <form onSubmit={submit} className="mt-8 grid gap-4">
          <label className="text-sm">Nuova password<input required minLength={12} type="password" className="field mt-1" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reset-password" /></label>
          <label className="text-sm">Codice dell'app di autenticazione o di recupero<input required className="field mt-1" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="reset-code" /></label>
          {state?.error && <p role="alert" className="text-sm text-red-700">{state.error}</p>}
          <button className="btn-primary justify-center" data-testid="reset-submit">Aggiorna password</button>
        </form>
      )}
    </Shell>
  );
}
